import * as React from 'react';
import { ISplitRow } from './splitLevelStrategies';
import './SplitSelectionTable.scss';

export const SPLIT_PAGE_SIZE = 10;

interface IProps {
    rows: ISplitRow[];
    numberLabel: string;
    label?: string;
    onChange: (selected: ISplitRow[]) => void;
}

interface IState {
    selectedIds: Set<any>;
    page: number;
    query: string;
    rowsSignature: string;
}

const idSignature = (rows: ISplitRow[]) => rows.map((r) => r.id).join('|');

/**
 * Multi-select list of split entities (SBK / FCR / ...), paginated so a charge split across
 * many bookings stays compact. Quantity is read-only (derived from the bookings response); the
 * user only chooses which entities the charge applies to. All rows start selected. Selection is
 * kept by row id, so it survives paging and searching, and select-all applies to every row that
 * matches the search (all pages). Selection is reported upward via onChange.
 */
export class SplitSelectionTable extends React.Component<IProps, IState> {
    public static getDerivedStateFromProps(props: IProps, state: IState): Partial<IState> | null {
        const rowsSignature = idSignature(props.rows);
        if (rowsSignature === state.rowsSignature) {
            return null;
        }
        // A different set of entities (e.g. another charge): start again with all selected.
        return {
            selectedIds: new Set(props.rows.map((r) => r.id)),
            page: 0,
            query: '',
            rowsSignature,
        };
    }

    constructor(props: IProps) {
        super(props);
        this.state = {
            selectedIds: new Set(props.rows.map((r) => r.id)),
            page: 0,
            query: '',
            rowsSignature: idSignature(props.rows),
        };
    }


    public getFilteredRows = (): ISplitRow[] => {
        const query = this.state.query.trim().toLowerCase();
        if (!query) {
            return this.props.rows;
        }
        return this.props.rows.filter((r) => String(r.number || '').toLowerCase().indexOf(query) !== -1);
    };

    public reportSelection = (selectedIds: Set<any>) => {
        this.props.onChange(this.props.rows.filter((r) => selectedIds.has(r.id)));
    };

    public updateSelection = (selectedIds: Set<any>) => {
        this.setState({ selectedIds });
        this.reportSelection(selectedIds);
    };

    public toggleRow = (id: any) => {
        const selectedIds = new Set(this.state.selectedIds);
        if (selectedIds.has(id)) {
            selectedIds.delete(id);
        } else {
            selectedIds.add(id);
        }
        this.updateSelection(selectedIds);
    };

    public toggleAll = () => {
        const filtered = this.getFilteredRows();
        const selectedIds = new Set(this.state.selectedIds);
        const allSelected = filtered.length > 0 && filtered.every((r) => selectedIds.has(r.id));
        filtered.forEach((r) => (allSelected ? selectedIds.delete(r.id) : selectedIds.add(r.id)));
        this.updateSelection(selectedIds);
    };

    public goToPage = (page: number) => {
        this.setState({ page });
    };

    public onSearch = (ev: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ query: ev.target.value, page: 0 });
    };

    public render() {
        const { rows, numberLabel, label } = this.props;
        const { selectedIds, query } = this.state;
        const filtered = this.getFilteredRows();
        const pageCount = Math.max(1, Math.ceil(filtered.length / SPLIT_PAGE_SIZE));
        const page = Math.min(this.state.page, pageCount - 1);
        const start = page * SPLIT_PAGE_SIZE;
        const visible = filtered.slice(start, start + SPLIT_PAGE_SIZE);
        const selectedInFilter = filtered.filter((r) => selectedIds.has(r.id)).length;
        const allSelected = filtered.length > 0 && selectedInFilter === filtered.length;
        const someSelected = selectedInFilter > 0 && !allSelected;
        const isPaged = rows.length > SPLIT_PAGE_SIZE;

        return (
            <div className="table-group-absolute" id="split-selection-table">
                <p>{label || 'Sub Bookings'}</p>
                {isPaged && (
                    <input
                        type="text"
                        className="split-search"
                        placeholder={`Search ${numberLabel}`}
                        value={query}
                        onChange={this.onSearch}
                    />
                )}
                <table className="split-grid">
                    <thead>
                        <tr>
                            <th className="split-check">
                                <input
                                    type="checkbox"
                                    className="split-select-all"
                                    checked={allSelected}
                                    ref={(el) => { if (el) { el.indeterminate = someSelected; } }}
                                    onChange={this.toggleAll}
                                />
                            </th>
                            <th>{numberLabel}</th>
                            <th className="-number">QTY</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visible.map((row) => (
                            <tr key={row.id}>
                                <td className="split-check">
                                    <input
                                        type="checkbox"
                                        className="split-row-check"
                                        checked={selectedIds.has(row.id)}
                                        onChange={() => this.toggleRow(row.id)}
                                    />
                                </td>
                                <td>{row.number}</td>
                                <td className="-number">{row.quantity}</td>
                            </tr>
                        ))}
                        {!visible.length && (
                            <tr>
                                <td colSpan={3} className="split-empty">No matching {numberLabel.toLowerCase()}</td>
                            </tr>
                        )}
                    </tbody>
                </table>
                <div className="split-footer">
                    <span className="split-count">{selectedIds.size} of {rows.length} selected</span>
                    {isPaged && (
                        <span className="split-pager">
                            <span className="split-range">
                                {filtered.length ? `${start + 1}-${start + visible.length}` : 0} of {filtered.length}
                            </span>
                            <button
                                type="button"
                                className="split-prev"
                                disabled={page === 0}
                                onClick={() => this.goToPage(page - 1)}
                            >
                                Prev
                            </button>
                            <span className="split-page">Page {page + 1} of {pageCount}</span>
                            <button
                                type="button"
                                className="split-next"
                                disabled={page >= pageCount - 1}
                                onClick={() => this.goToPage(page + 1)}
                            >
                                Next
                            </button>
                        </span>
                    )}
                </div>
            </div>
        );
    }
}
